import type { FunctionComponent } from "react";
import { useMemo, useRef, type JSX } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import queryString from "query-string";
import { noop } from "lodash";

import type { IChangeFilterItems } from "app/components/filters-bar/change-filter/ChangeFilter";
import { EChangeFilterValues } from "app/components/filters-bar/change-filter/ChangeFilter";
import { ChangeFilterForWebsiteKeywords } from "app/pages/website-analysis/traffic-sources/search/components/filters/ChangeFilterForWebsiteKeywords";
import { swSettings } from "app/common/services/swSettings";
import type { IWebsiteKeywordsPageTableTopContext } from "../../../../website-analysis/traffic-sources/search/components/WebsiteKeywordsPageContext";
import { WebsiteKeywordsPageTableTopContextProvider } from "../../../../website-analysis/traffic-sources/search/components/WebsiteKeywordsPageContext";
import { useDispatch } from "react-redux";
import { fullMonthAndYear, shortMonthAndDay } from "@similarweb/pro-logic-components";
import type { IColumnsPickerLiteProps } from "@similarweb/ui-components";
import { ColumnsPickerLite } from "@similarweb/ui-components";
import { chosenSites } from "app/common/services/chosenSites";

import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { SerpFilter } from "../../../../website-analysis/traffic-sources/search/components/filters/SerpFilter";
import {
    SERP_IDS,
    getSerpMapByKeys,
} from "../../../../website-analysis/traffic-sources/search/components/serp/SerpTypes";
import { PageCompareTabsContainer } from "app/components/compareTabs/PageCompareTabsContainer";
import {
    getDomainWithSubdomain,
    getUrlComponents,
} from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { ELandingPagesTabs } from "app/common/keyword-research/landing-pages/landingPagesTypes";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { DownloadExcelContainer } from "app/pages/workspace/StyledComponent";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService, { customRangeFormat } from "app/services/DurationService";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import {
    MultiIncludeExcludeKeywordsFilter,
    MultiIncludeExcludeWebsitesFilter,
} from "../../../../website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/MultiIncludeExcludeConnected";
import { getWebsiteKeywordsPageTableTopContext } from "../../../../website-analysis/traffic-sources/search/components/WebsiteKeywordsPageUtillities";

import {
    FiltersWrapper,
    OrganicLandingPagesTableHeaderStyled,
    OverviewWrapper,
    TableTopWrapper,
    Title,
} from "./StyledComponents";
import { useLandingPagesEndpoints } from "./landingPagesEndpoints";

const i18n = i18nFilter();
const DASHBOARD_TYPE = "TableLandingPageDashboard";
const DEFAULT_SORT = "PageShare";

export const availableSerpFeatures = getSerpMapByKeys(
    SERP_IDS.ORGANIC_SITE_LINKS,
    SERP_IDS.NEWS,
    SERP_IDS.VIDEO,
    SERP_IDS.FEATURED_ANSWER,
    SERP_IDS.FEATURED_LIST,
    SERP_IDS.FEATURED_TABLE,
    SERP_IDS.TWITTER_FEED,
);

export const availableSerpPaidFeatures = getSerpMapByKeys(
    SERP_IDS.PAID,
    SERP_IDS.PAID_SITE_LINKS,
    SERP_IDS.PRODUCT_LISTING_ADS,
);

interface ITableColumn {
    fixed?: boolean;
    cellComponent: JSX.Element;
    disableHeaderCellHover?: boolean;
    sortable?: boolean;
    width?: number;
    isResizable?: boolean;
    hideInDashboard?: boolean;
    field: string;
    tooltip: string;
    displayName: string;
    cellClass?: string;
    minWidth?: number;
    maxWidth?: number;
    visible?: boolean;
}

interface IOrganicLandingPagesTableTopBarV2Props {
    sort: string;
    asc: boolean;
    tableColumns: ITableColumn[];
    onFilterChange: (...args: any[]) => void;
    onClickToggleColumns: (index: number) => void;
    excelDownloadUrl: string;
    excelRequestBody: any;
    excelAllowed: boolean;
    filtersStateObject: Record<string, any>;
    isLoadingData: boolean;
    sourceType: string;
    totalCount: number;
    withAddToDashboard?: boolean;
}

import { changeFilterItems } from "app/common/keyword-research/landing-pages/landingPagesFilterConsts";
import { allTrackers } from "app/services/track/track";

export const OrganicLandingPagesTableTopBarV2: FunctionComponent<IOrganicLandingPagesTableTopBarV2Props> = (props) => {
    const {
        sort = DEFAULT_SORT,
        asc = false,
        tableColumns,
        onFilterChange,
        onClickToggleColumns,
        filtersStateObject,
        excelAllowed,
        excelDownloadUrl,
        isLoadingData,
        excelRequestBody,
        totalCount,
        withAddToDashboard = true,
    } = props;
    const endpoints = useLandingPagesEndpoints();
    const dispatch = useDispatch();
    const { params } = useCurrentStateAndParams();
    const { selectedDomain, key, webSource, duration, country, selectedPageTab, serp, ...addToDashboardParams } =
        params;
    const initialFiltersStateObject = useRef(filtersStateObject);
    const { from, to, isWindow } = DurationService.getDurationData(params.duration).forAPI;
    const serpFeatures =
        selectedPageTab === ELandingPagesTabs[ELandingPagesTabs.Organic]
            ? availableSerpFeatures
            : availableSerpPaidFeatures;
    const urls: IChosenItemParams | string[] = params.pageFilter ? JSON.parse(params.pageFilter) : key.split(",");
    const isCompare = urls?.length > 1;
    const selectedSerpFeatures = serp?.split?.(",").filter((id) => serpFeatures[id]);

    const queryParamsForCompareTabs = {
        from,
        to,
        isWindow,
        key: params.key,
        webSource: params.webSource,
        includeSubDomains: params.isWWW === "*",
        country: params.country,
        Change: params.Change,
        serp: selectedSerpFeatures?.join?.(","),
        pageFilterJson: params.pageFilter,
        sourceType: SourceTypeMap[params.selectedPageTab],
        multiIncludeExcludeKeywords: params.multiIncludeExcludeKeywords,
        multiIncludeExcludeWebsites: params.multiIncludeExcludeWebsites,
    };

    const selectedPage = selectedDomain ?? (typeof urls[0] === "string" ? urls[0] : urls[0].url);
    const selectedDomainUrlComponents = getUrlComponents(selectedPage);
    const selectedDomainKey = getDomainWithSubdomain(
        selectedDomainUrlComponents.domain,
        selectedDomainUrlComponents.subdomain,
    );
    const chosenSiteInfo = chosenSites.getInfo(selectedDomainKey);
    const chosenPageFilter = urls.find((url) =>
        typeof url === "string" ? url === selectedPage : url?.url === selectedPage,
    );
    addToDashboardParams.pageFilter = JSON.stringify([chosenPageFilter]);

    const queryParams = { ...filtersStateObject, sort, asc };
    let excelDownloadUrlParams;
    if (excelAllowed) {
        excelDownloadUrlParams = `${excelDownloadUrl}?${queryString.stringify(queryParams)}`;
    }

    const onColumnToggle = (columnKey: string) => {
        allTrackers.dispatchTrackEvent("Button", "click", `table column picker/${columnKey || ""}`);
        onClickToggleColumns(parseInt(columnKey));
    };

    const getColumnsPickerLiteProps = (): Omit<IColumnsPickerLiteProps, "translations"> => {
        const columns = tableColumns.reduce((res, col, index) => {
            if (!col.fixed) {
                return [
                    ...res,
                    {
                        key: index.toString(),
                        displayName: col.displayName,
                        visible: col.visible,
                    },
                ];
            }
            return res;
        }, []);
        return {
            columns,
            onColumnToggle,
            onPickerToggle: noop,
            maxHeight: 264,
            width: "auto",
        };
    };

    const onTabSelectSite = (updatedSelectedDomain: string, numberOfResults: number) => {
        allTrackers.dispatchTrackEvent(
            "Tab",
            "click",
            `${updatedSelectedDomain || ""} tab with ${numberOfResults?.toString() || ""} was selected`,
        );
        proNavigator.updateParams({ selectedDomain: updatedSelectedDomain });
    };

    const trackExcelDownload = () => {
        allTrackers.dispatchTrackEvent("Download", "click", "Table/Excel");
    };

    const contextValue = useMemo<IWebsiteKeywordsPageTableTopContext>(() => {
        const websiteKeywordsPageTableTopContextArgs = {
            isLoadingData,
            initialFiltersStateObject,
            isCompare,
            chosenItems: chosenSites.sitelistForLegend(),
            nextTableParams: {
                multiIncludeExcludeKeywords: params.multiIncludeExcludeKeywords,
                multiIncludeExcludeWebsites: params.multiIncludeExcludeWebsites,
                Change: params.Change,
            },
            addTempParams: (values) => {
                proNavigator.updateParams(values);
            },
            dispatch,
        };
        return getWebsiteKeywordsPageTableTopContext(websiteKeywordsPageTableTopContextArgs);
    }, [isLoadingData, isCompare]);

    const handleSerpFeaturesApply = (updatedSerpFeatures: string[]) => {
        onFilterChange({ serp: updatedSerpFeatures.join(",") }, true);
    };

    return (
        <TableTopWrapper>
            <WebsiteKeywordsPageTableTopContextProvider value={contextValue}>
                <FiltersWrapper>
                    <MultiIncludeExcludeWebsitesFilter />
                    <MultiIncludeExcludeKeywordsFilter />
                    <ChangeFilterForWebsiteKeywords changeFilterItems={changeFilterItems} />
                    <SerpFilter
                        tableFilters={{ serp: selectedSerpFeatures }}
                        resetEnabled
                        availableSerpFeatures={serpFeatures}
                        onSerpFilterApply={handleSerpFeaturesApply}
                    />
                </FiltersWrapper>
            </WebsiteKeywordsPageTableTopContextProvider>
            <OverviewWrapper>
                <OrganicLandingPagesTableHeaderStyled>
                    <Title>
                        {isWindow
                            ? i18n("keywordanalysis.2.organic.landing.pages.table.window.title", {
                                  date: swSettings.current.windowEndDate.format(shortMonthAndDay),
                              })
                            : i18n("keywordanalysis.2.organic.landing.pages.table.title", {
                                  date: dayjs.utc(params.duration, customRangeFormat).format(fullMonthAndYear),
                              })}
                    </Title>
                    <FlexRow>
                        <DownloadExcelContainer>
                            <DownloadButtonMenu
                                Excel={true}
                                PaidExcel={isPaidExcelAllowed()}
                                totalCount={totalCount}
                                downloadUrl={excelDownloadUrlParams}
                                excelBodyForReq={excelRequestBody}
                                exportFunction={trackExcelDownload}
                                excelLocked={!excelAllowed}
                            />
                        </DownloadExcelContainer>
                        <ColumnsPickerLite
                            {...getColumnsPickerLiteProps()}
                            translations={{
                                tooltip: i18n("workspace.sales.column.picker"),
                                getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                    i18n("common.columnsPicker.selectedAmount", {
                                        selected: selectedValue,
                                        total: totalValue,
                                    }),
                                mandatoryField: i18n("common.columnsPicker.mandatoryField"),
                                apply: i18n("common.apply"),
                            }}
                        />
                        {withAddToDashboard ? (
                            <AddToDashboard
                                webSource={webSource}
                                modelType="fromWebsite"
                                metric={DASHBOARD_TYPE}
                                type={DASHBOARD_TYPE}
                                filters={{
                                    sourceType: SourceTypeMap[params.selectedPageTab],
                                    multiIncludeExcludeKeywords: params.multiIncludeExcludeKeywords,
                                    multiIncludeExcludeWebsites: params.multiIncludeExcludeWebsites,
                                    Change: params.Change,
                                }}
                                duration={duration}
                                country={country}
                                overrideAddToDashboardParams={{
                                    key: [
                                        {
                                            name: chosenSiteInfo.name,
                                            displayName: chosenSiteInfo.displayName,
                                            image: chosenSiteInfo.icon,
                                        },
                                    ],
                                }}
                            />
                        ) : null}
                    </FlexRow>
                </OrganicLandingPagesTableHeaderStyled>
                {isCompare ? (
                    <PageCompareTabsContainer
                        onSelectSite={onTabSelectSite}
                        keyName="OrganicLP2FirstLoad"
                        api={endpoints.totalPagesPerSite}
                        domainQueryParamName={"selectedDomain"}
                        queryParamsForCompareTabs={queryParamsForCompareTabs}
                        requestBody={excelRequestBody}
                    />
                ) : null}
            </OverviewWrapper>
        </TableTopWrapper>
    );
};
